import { Link } from "react-router-dom";
import { COMPANY } from "../../lib/company";
import { PARTNERS } from "../partners";
import { PartnerCta } from "../PartnerCta";

/**
 * Graphaize — the creative partner one-pager.
 *
 * A one-pager in the sales sense: everything a prospect needs about the partner on a
 * single scroll — who they are, what they make, how they work, where it fits in the
 * funnel, and how to reach them. It is the destination for the argument made on the
 * {@link ContentCreator} page.
 *
 * Visual vocabulary is the Landing page's, unchanged: `.hero`, `.strip`, `.band` /
 * `.band.alt`, `.works-band` steps, the feature `.grid` and `.cta`. `PublicLayout`
 * supplies the header, footer and `<main>`.
 *
 * Every fact below is Graphaize's own published description of itself, sourced from
 * graphaize.com and attributed as such at the foot of the page. We have not measured
 * their results, so no outcome numbers are claimed here — stating a conversion lift we
 * cannot evidence would be the one thing on this page a reader could catch us on.
 */

/**
 * Graphaize's published identity. Their own phone and mailbox are deliberately
 * NOT here: enquiries route through /contact so the introduction is made by us,
 * and an address we do not publish is one that cannot go stale on this page.
 */
const GRAPHAIZE = {
  name: "Graphaize",
  tagline: "Premium Visual Content and Explainer Video",
  /** From the site's own copyright range, "2015-2026". */
  since: "2015",
  /**
   * Their own logo files, copied into `public/partners/` rather than hot-linked:
   * a page that loses its partner's branding because someone reorganised a
   * WordPress uploads folder is not a page we control.
   *
   * The wordmark is white on transparent — it is drawn for their own dark header
   * — so it may only ever be placed on the navy plate below, never on `--panel`.
   * The mark is the colour icon from their favicon, on white, for light plates.
   */
  logo: "/partners/graphaize-logo.png",
  mark: "/partners/graphaize-mark.jpg",
} as const;

/** Their three-step production process, as they describe it. */
const PROCESS = [
  {
    n: "01",
    title: "Concept",
    body: "They analyse what the piece has to achieve and come back with several creative concepts rather than one take-it-or-leave-it idea.",
    tag: "Understand first. Then propose.",
  },
  {
    n: "02",
    title: "Visualize",
    body: "The chosen concept becomes a storyline with imagery and a defined style — the point at which a vague brief turns into something you can actually judge.",
    tag: "Storyline · Imagery · Style",
  },
  {
    n: "03",
    title: "Animate",
    body: "Graphics, animation and sound design are refined into the finished piece, ready for the channel it was made for.",
    tag: "Motion. Sound. Delivered.",
  },
];

/** What they make. Their published service list, grouped into six cards. */
const SERVICES = [
  {
    icon: "▶",
    title: "Explainer videos",
    body: "Their core offering: taking an unusual or technical product and making it understood in a single watch, instead of another demo call.",
  },
  {
    icon: "◫",
    title: "Infographics",
    body: "Dense information laid out so the argument is visible at a glance — useful where a page of copy loses the reader.",
  },
  {
    icon: "◉",
    title: "Educational and onboarding video",
    body: "Training and client-onboarding material, for the explanations a team would otherwise repeat by hand every week.",
  },
  {
    icon: "✦",
    title: "Promotional video and social ads",
    body: "Short promotional pieces and paid social creative, cut for the placement rather than resized after the fact.",
  },
  {
    icon: "▤",
    title: "Pitch decks and ebooks",
    body: "Investor and sales decks, and longer-form ebooks — the documents that have to carry your case when nobody is in the room to present it.",
  },
  {
    icon: "◈",
    title: "Interactive microsites",
    body: "Small standalone web experiences for a launch or a campaign, where a landing page is not enough and a full site is too much.",
  },
];

/**
 * The buyer-journey stages they map their work onto. Classic AIDA, which is how they
 * frame the service list on their own site.
 */
const FUNNEL = [
  { stage: "Awareness", title: "Get noticed", body: "Give new audiences a reason to stop scrolling.", formats: "Social creative · Promo videos" },
  { stage: "Interest", title: "Make it click", body: "Turn a complex product into a clear story.", formats: "Explainers · Infographics" },
  { stage: "Desire", title: "Build conviction", body: "Show people why your offer fits their needs.", formats: "Pitch decks · Ebooks · Microsites" },
  { stage: "Action", title: "Guide the next step", body: "Help new customers get started with confidence.", formats: "Educational video · Onboarding" },
];

export function Graphaize() {
  return (
    /* Everything inside wears Graphaize's palette: `.partner-graphaize` rebinds
       `--accent` and the ramp to their brand tokens, so the Landing page's own
       classes render in their colours without a single one being forked. */
    <div className="partner-graphaize">
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-copy">
            {/* One chip, not a badge sitting next to a plate. The label and the
                wordmark share a pill, and the navy inlay is only there because
                their logo is white ink and needs something to sit on. */}
            <p className="hero-badge gz-partner-chip">
              <span aria-hidden="true">✦</span>
              <span className="gz-chip-label">Creative partner</span>
              <span className="gz-chip-logo">
                <img src={GRAPHAIZE.logo} alt={`${GRAPHAIZE.name} logo`} width="293" height="50" />
              </span>
            </p>
            <h1>
              <span>{GRAPHAIZE.name} &mdash;</span>
              <span className="hero-gradient-text">premium visual content</span>
              <span>&amp; explainer video.</span>
            </h1>
            <p className="lede">
              The studio we point brands at when the product is genuinely hard to explain.
              Their argument, in their words: complex offerings lose leads because prospects
              never quite understand them — so turn the complexity into something watchable.
            </p>
            <div className="hero-cta">
              <PartnerCta partner={PARTNERS.graphaize} className="btn-link primary lg">
                Book a call with their strategist <span aria-hidden="true">→</span>
              </PartnerCta>
              <Link to="/partners/content-creator" className="btn-link lg">
                Why a human creator <span aria-hidden="true">◉</span>
              </Link>
            </div>
            <div className="hero-benefits" aria-label="Partner at a glance">
              <span><i className="benefit-icon violet" aria-hidden="true">✦</i> Working<br />since {GRAPHAIZE.since}</span>
              <span><i className="benefit-icon blue" aria-hidden="true">▶</i> Explainer<br />video specialists</span>
              <span><i className="benefit-icon green" aria-hidden="true">◫</i> Full funnel<br />coverage</span>
            </div>
          </div>

          <div className="hero-visual">
            <section className="partner-panel partner-panel-hero" aria-labelledby="gz-facts-title">
              <div className="gz-panel-head">
                <img className="gz-mark" src={GRAPHAIZE.mark} alt="" width="56" height="56" />
                <div>
                  <p className="eyebrow">At a glance</p>
                  <h2 id="gz-facts-title">{GRAPHAIZE.tagline}</h2>
                </div>
              </div>
              <dl className="partner-facts">
                <div>
                  <dt>Studio</dt>
                  <dd>{GRAPHAIZE.name}</dd>
                </div>
                <div>
                  <dt>Operating since</dt>
                  <dd>{GRAPHAIZE.since}</dd>
                </div>
                <div>
                  <dt>Specialism</dt>
                  <dd>Explainer &amp; educational video</dd>
                </div>
                <div>
                  <dt>Also makes</dt>
                  <dd>Infographics, pitch decks, ebooks, microsites, social ads</dd>
                </div>
                <div>
                  <dt>Process</dt>
                  <dd>Concept → Visualize → Animate</dd>
                </div>
                <div>
                  <dt>Best for</dt>
                  <dd>Products that take three calls to explain</dd>
                </div>
              </dl>
            </section>
          </div>
        </div>
      </section>

      <section className="strip">
        <div className="strip-inner">
          <div className="platforms-title"><i aria-hidden="true">◇</i><span>What they make</span></div>
          <div className="partner-tags">
            <span className="partner-tag">Explainer videos</span>
            <span className="partner-tag">Infographics</span>
            <span className="partner-tag">Educational video</span>
            <span className="partner-tag">Promotional video</span>
            <span className="partner-tag">Pitch decks</span>
            <span className="partner-tag">Ebooks</span>
            <span className="partner-tag">Microsites</span>
            <span className="partner-tag">Social ads</span>
          </div>
        </div>
      </section>

      <section className="band">
        <div className="band-inner narrow">
          <h2 className="section-title">The problem they solve</h2>
          <p className="lede">
            Some products cannot be explained in a sentence. The founder explains it
            beautifully on a call, the website does not, and the gap between the two is
            paid for in leads that never convert and hours spent repeating the same demo.
          </p>
          <p>
            Graphaize&rsquo;s whole pitch is to close that gap once: take the complicated
            thing, find the version of it that a stranger understands in ninety seconds,
            and produce it well enough to put in front of the people you are trying to win.
            In their words, <em>&ldquo;from complex ideas to winning visuals.&rdquo;</em>
          </p>
        </div>
      </section>

      <section className="band alt works-band">
        <div className="band-inner">
          <div className="works-head">
            <p className="hero-badge"><span aria-hidden="true">✦</span> Their process</p>
            <h2 className="section-title">Concept, visualize, <span>animate</span></h2>
            <p>Three stages, with something reviewable at the end of each one.</p>
          </div>
          <div className="steps">
            {PROCESS.map(s => (
              <div className="step" key={s.n}>
                <span className="step-n">{s.n}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
                <span className="step-tag"><i aria-hidden="true">✓</i>{s.tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band features-band">
        <div className="band-inner">
          <h2 className="section-title">Services</h2>
          <p className="lede">
            One studio across the formats most brands otherwise scatter over three freelancers.
          </p>
          <div className="grid">
            {SERVICES.map(s => (
              <div className="feature" key={s.title}>
                <span className="feature-icon" aria-hidden="true">{s.icon}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band alt">
        <div className="band-inner">
          <h2 className="section-title">Mapped to the buyer&rsquo;s journey</h2>
          <p className="lede">
            The right creative for each step, from first impression to getting started.
          </p>
          <ol className="partner-stages">
            {FUNNEL.map((f, i) => (
              <li key={f.stage} className="partner-stage">
                <span className="partner-stage-n" aria-hidden="true">{i + 1}</span>
                <span className="gz-stage-label">{f.stage}</span>
                <h3>{f.title}</h3>
                <p>{f.body}</p>
                <span className="gz-stage-formats">{f.formats}</span>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="band">
        <div className="band-inner">
          <div className="gz-fit-card">
            <div className="gz-fit-intro">
              <p className="eyebrow">Graphaize + {COMPANY.product}</p>
              <h2 className="section-title">Great content.<br />Ready to reach people.</h2>
              <p className="lede">Bring your story to life, then keep it in front of your audience.</p>
              <PartnerCta partner={PARTNERS.graphaize} className="btn-link primary">
                Start a {GRAPHAIZE.name} enquiry <span aria-hidden="true">&rarr;</span>
              </PartnerCta>
            </div>
            <div className="gz-fit-benefits">
              <h3 className="gz-fit-heading">Why you need both</h3>
              <div className="gz-fit-benefit">
                <span className="partner-stage-n" aria-hidden="true">01</span>
                <div>
                  <h3>Make your message matter</h3>
                  <p>Graphaize turns your ideas into videos and visuals people can understand.</p>
                  <Link to="/partners/content-creator">Why a human creator <span aria-hidden="true">↗</span></Link>
                </div>
              </div>
              <div className="gz-fit-benefit">
                <span className="partner-stage-n" aria-hidden="true">02</span>
                <div>
                  <h3>Keep your channels active</h3>
                  <p>Compose once in {COMPANY.product}. Publish to Facebook Pages, Instagram Business and YouTube, with status for each destination.</p>
                  <Link to="/how-it-works">How publishing works <span aria-hidden="true">↗</span></Link>
                </div>
              </div>
            </div>
            <p className="gz-fit-contact">We make the introduction. Graphaize is an independent studio; your engagement is directly with them.</p>
          </div>
          <p className="partner-source">
            Details on this page are Graphaize&rsquo;s own published description of their
            studio and services, as published on their own site and current as of
            20 September 2026. {COMPANY.product} has not independently verified their
            results, and no outcome is claimed on their behalf.
          </p>
        </div>
      </section>

      <section className="cta">
        <div className="cta-inner">
          <span className="cta-social cta-instagram" aria-hidden="true">◎</span>
          <span className="cta-social cta-facebook" aria-hidden="true">f</span>
          <span className="cta-social cta-youtube" aria-hidden="true">▶</span>
          <h2>Complex product? Make it watchable.</h2>
          <p>
            Talk to Graphaize about the piece your website cannot do on its own &mdash; then
            publish it everywhere from {COMPANY.product}.
          </p>
          <PartnerCta partner={PARTNERS.graphaize} className="btn-link primary lg">
            Book a call with their strategist <span aria-hidden="true">→</span>
          </PartnerCta>
          <div className="cta-assurances" aria-label="About this partner">
            <span><i aria-hidden="true">✓</i>Independent studio</span>
            <span><i aria-hidden="true">✓</i>Working since {GRAPHAIZE.since}</span>
            <span><i aria-hidden="true">✓</i>Full-funnel formats</span>
          </div>
        </div>
      </section>
    </div>
  );
}
